import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useRouteAnalytics } from '@/lib/analytics';
import { Toaster } from 'sonner';
import { useAuthStore } from '@/stores/auth';

// Layouts
import DashboardLayout from '@/components/layouts/DashboardLayout';
import AuthLayout from '@/components/layouts/AuthLayout';

// Auth pages
import LoginPage from '@/pages/auth/Login';
import RegisterPage from '@/pages/auth/Register';
import ForgotPasswordPage from '@/pages/auth/ForgotPassword';
import SetPasswordPage from '@/pages/auth/SetPassword';
import AuthCallbackPage from '@/pages/auth/AuthCallback';
import VerifyEmailPage from '@/pages/auth/VerifyEmail';
import GhostLoginPage from '@/pages/auth/GhostLogin';

// Dashboard pages
import DashboardPage from '@/pages/dashboard/Dashboard';
import ProjectsPage from '@/pages/dashboard/Projects';
import ProjectDetailPage from '@/pages/dashboard/ProjectDetail';
import UsersPage from '@/pages/dashboard/Users';
import PluginsPage from '@/pages/dashboard/Plugins';
import SettingsPage from '@/pages/dashboard/Settings';
// YOM-backed when the site is provisioned; falls back to the legacy page.
import AnalyticsPage from '@/pages/project/Analytics';

// Project-specific pages
import NewsletterPage from '@/pages/project/Newsletter';
import CampaignsPage from '@/pages/project/Campaigns';
import ContactsPage from '@/pages/project/Contacts';
import ConsentPage from '@/pages/project/Consent';
import BlogPage from '@/pages/project/Blog';
import BlogSuggestionsPage from '@/pages/project/BlogSuggestions';
import BlogTopicMapPage from '@/pages/project/BlogTopicMap';
import BlogPostEditorPage from '@/pages/project/BlogPostEditor';
import DnsMigrationWizard from '@/pages/project/DnsMigrationWizard';
import AITrainingPage from '@/pages/AITraining';
import ChatbotPage from '@/pages/project/Chatbot';

// E-Commerce pages
import ProductsPage from '@/pages/project/Products';
import OrdersPage from '@/pages/project/Orders';
import OrderDetailPage from '@/pages/project/OrderDetail';
import CommerceSettingsPage from '@/pages/project/CommerceSettings';
import CouponsPage from '@/pages/project/Coupons';

// CRM pages
import CRMPage from '@/pages/project/CRM';
import CRMContactDetailPage from '@/pages/project/CRMContactDetail';
import CRMPipelinePage from '@/pages/project/CRMPipeline';
import CRMSettingsPage from '@/pages/project/CRMSettings';

// Plugin detail (universal)
import PluginDetailPage from '@/pages/project/PluginDetail';
import SpeedBoosterPage from '@/pages/project/SpeedBooster';
import { ConfirmProvider } from '@/components/ui/confirm-provider';
import SeoCopilotDashboard from '@/pages/project/SeoCopilotDashboard';
import AeoDashboard from '@/pages/project/AeoDashboard';
import SeoAutopilotDashboard from '@/pages/project/SeoAutopilotDashboard';

// Webhooks admin
import WebhooksPage from '@/pages/project/Webhooks';

// Audit log
import AuditLogPage from '@/pages/project/AuditLog';

// Request dashboard
import RequestDashboardPage from '@/pages/project/RequestDashboard';

// Brand Kit (colors, logo, fonts, radius — drives Tyashin-owned pages + brand-aware storefronts)
import BrandKitPage from '@/pages/project/BrandKit';

// Page Ownership (per-project toggle for Tyashin-owned vs storefront-owned platform pages)
import PageOwnershipPage from '@/pages/project/PageOwnership';

// Storefront compliance + SEO admin pages
import FaqPage from '@/pages/project/Faq';
import InstagramReelsPage from '@/pages/project/InstagramReels';
import TestimonialsPage from '@/pages/project/Testimonials';
import PrivacyRequestsPage from '@/pages/project/PrivacyRequests';
import ReviewsPage from '@/pages/project/Reviews';
import ReturnsPage from '@/pages/project/Returns';
import ErrorsPage from '@/pages/project/Errors';

// Settings pages
import TeamSettingsPage from '@/pages/settings/Team';
import BillingSettingsPage from '@/pages/settings/Billing';
import BillingInvoicesPage from '@/pages/settings/BillingInvoices';

// Admin pages
import SREDashboardPage from '@/pages/admin/SREDashboard';
import AdminBillingPage from '@/pages/admin/AdminBilling';
import PaymentLinksPage from '@/pages/admin/PaymentLinks';
import PaymentLinkPage from '@/pages/pay/PaymentLinkPage';
import CostMonitorPage from '@/pages/admin/CostMonitor';
import AdminIntegrationsPage from '@/pages/admin/Integrations';
import PlatformInsightsPage from '@/pages/admin/PlatformInsights';
import SubscriberRegisterPage from '@/pages/admin/SubscriberRegister';

// Invite page
import InviteAcceptPage from '@/pages/auth/InviteAccept';
import DeviceApprovePage from '@/pages/auth/DeviceApprove';
// GitHub OAuth popup callback
import GitHubCallbackPage from '@/pages/auth/GitHubCallback';

// Editor page (fullscreen, outside DashboardLayout)
import WebsiteEditorPage from '@/pages/editor/WebsiteEditor';
// Website Studio — the customer journey (welcome wizard → build → refine)
import WelcomePage from '@/pages/onboarding/Welcome';
import WebsiteStudioPage from '@/pages/project/WebsiteStudio';
import WebsiteRedirectPage from '@/pages/project/WebsiteRedirect';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, _hasHydrated } = useAuthStore();
  const location = useLocation();

  // Wait for hydration first
  if (!_hasHydrated || isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Keep where they were going (e.g. the "connect your domain" link the
    // Tyashin Claude plugin hands out: /projects/:id?tab=domains) so login
    // lands them there instead of on the dashboard.
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}

/** Platform-admin pages: the sidebar already hides them, but the routes must
 *  refuse too — otherwise a customer who types /admin/… sees a half-rendered
 *  console full of 403 errors. */
function RequireSuperAdmin({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  if (user?.role !== 'SUPER_ADMIN') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, _hasHydrated } = useAuthStore();

  // Wait for hydration first
  if (!_hasHydrated || isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

/** /projects/:id/seo → the SEO dashboard (where the Search engines card lives). */
function SeoRedirect() {
  const { projectId } = useParams<{ projectId: string }>();
  return <Navigate to={`/projects/${projectId}/plugins/seo-copilot/dashboard`} replace />;
}

export default function App() {
  useRouteAnalytics(); // T-APP view events (editor_opened, report_viewed, …)
  return (
    <ConfirmProvider>
      <Toaster position="top-right" richColors />
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <AuthLayout>
                <LoginPage />
              </AuthLayout>
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <AuthLayout>
                <RegisterPage />
              </AuthLayout>
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <AuthLayout>
                <ForgotPasswordPage />
              </AuthLayout>
            </PublicRoute>
          }
        />

        {/* Set password (admin-created users — no auth required, standalone page) */}
        <Route path="/set-password" element={<SetPasswordPage />} />

        {/* Also handle /reset-password with the same page (forgot-password flow) */}
        <Route path="/reset-password" element={<SetPasswordPage />} />

        {/* Auth callback (for OAuth/magic link redirects) */}
        <Route path="/auth/callback" element={<AuthCallbackPage />} />

        {/* Signup email verification. Deliberately NOT wrapped in PublicRoute:
            the link is just as likely to be opened in a tab that is already
            signed in, and PublicRoute would bounce it to /dashboard, throwing
            the token away exactly as the catch-all used to. */}
        <Route path="/verify-email" element={<VerifyEmailPage />} />

        {/* GitHub OAuth callback (popup) */}
        <Route path="/auth/github-callback" element={<GitHubCallbackPage />} />

        {/* Ghost login (admin impersonation — opens in new tab) */}
        <Route path="/auth/ghost-login" element={<GhostLoginPage />} />

        {/* Invite accept (handles its own auth redirect) */}
        <Route path="/invite" element={<InviteAcceptPage />} />

        {/* Device sign-in for the customer's own tools (handles its own auth redirect) */}
        <Route path="/device" element={<DeviceApprovePage />} />

        {/* Public payment link landing (no auth — the token is the secret) */}
        <Route path="/pay/:token" element={<PaymentLinkPage />} />

        {/* Welcome wizard (fullscreen, no sidebar) — first run after signup */}
        <Route
          path="/welcome"
          element={
            <ProtectedRoute>
              <WelcomePage />
            </ProtectedRoute>
          }
        />

        {/* Editor route (fullscreen, no sidebar) */}
        <Route
          path="/projects/:projectId/editor/:jobId"
          element={
            <ProtectedRoute>
              <WebsiteEditorPage />
            </ProtectedRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="website" element={<WebsiteRedirectPage />} />
          <Route path="projects" element={<ProjectsPage />} />
          <Route path="projects/:projectId/website" element={<WebsiteStudioPage />} />
          <Route path="projects/:projectId" element={<ProjectDetailPage />} />
          <Route path="projects/:projectId/newsletter" element={<NewsletterPage />} />
          <Route path="projects/:projectId/newsletter/campaigns" element={<CampaignsPage />} />
          <Route path="projects/:projectId/contacts" element={<ContactsPage />} />
          <Route path="projects/:projectId/consent" element={<ConsentPage />} />
          <Route path="projects/:projectId/analytics" element={<AnalyticsPage />} />
          <Route path="projects/:projectId/blog" element={<BlogPage />} />
          <Route path="projects/:projectId/blog/suggestions" element={<BlogSuggestionsPage />} />
          <Route path="projects/:projectId/blog/topic-map" element={<BlogTopicMapPage />} />
          <Route path="projects/:projectId/blog/new" element={<BlogPostEditorPage />} />
          <Route path="projects/:projectId/blog/:postId/edit" element={<BlogPostEditorPage />} />
          <Route path="projects/:projectId/speed-booster" element={<SpeedBoosterPage />} />
          <Route path="projects/:projectId/dns-migration" element={<DnsMigrationWizard />} />
          {/* E-Commerce routes */}
          <Route path="projects/:projectId/ecommerce" element={<ProductsPage />} />
          <Route path="projects/:projectId/ecommerce/orders" element={<OrdersPage />} />
          <Route
            path="projects/:projectId/ecommerce/orders/:orderId"
            element={<OrderDetailPage />}
          />
          <Route path="projects/:projectId/ecommerce/coupons" element={<CouponsPage />} />
          <Route path="projects/:projectId/ecommerce/settings" element={<CommerceSettingsPage />} />
          {/* CRM routes */}
          <Route path="projects/:projectId/crm" element={<CRMPage />} />
          <Route
            path="projects/:projectId/crm/contacts/:contactId"
            element={<CRMContactDetailPage />}
          />
          <Route path="projects/:projectId/crm/pipeline" element={<CRMPipelinePage />} />
          <Route path="projects/:projectId/crm/settings" element={<CRMSettingsPage />} />
          {/* Universal plugin detail page */}
          <Route
            path="projects/:projectId/plugins/seo-copilot/dashboard"
            element={<SeoCopilotDashboard />}
          />
          <Route
            path="projects/:projectId/plugins/seo-copilot/autopilot"
            element={<SeoAutopilotDashboard />}
          />
          <Route path="projects/:projectId/plugins/aeo/dashboard" element={<AeoDashboard />} />
          {/* Project-scoped plugin marketplace (no dropdown, pre-knows installed state) */}
          <Route path="projects/:projectId/plugins" element={<PluginsPage />} />
          <Route path="projects/:projectId/plugins/:pluginId" element={<PluginDetailPage />} />
          {/* Brand kit (colors, logo, fonts, radius) */}
          <Route path="projects/:projectId/brand-kit" element={<BrandKitPage />} />
          {/* Page ownership (per-page Tyashin/Storefront toggle) */}
          <Route path="projects/:projectId/page-ownership" element={<PageOwnershipPage />} />

          <Route path="projects/:projectId/faq" element={<FaqPage />} />
          <Route path="projects/:projectId/instagram-reels" element={<InstagramReelsPage />} />
          <Route path="projects/:projectId/testimonials" element={<TestimonialsPage />} />
          <Route path="projects/:projectId/privacy-requests" element={<PrivacyRequestsPage />} />
          <Route path="projects/:projectId/reviews" element={<ReviewsPage />} />
          <Route path="projects/:projectId/returns" element={<ReturnsPage />} />
          <Route path="projects/:projectId/errors" element={<ErrorsPage />} />
          {/* The Verification tab is gone — ownership is proven by the Search
              engines card on the SEO dashboard. Old deep links land there. */}
          <Route path="projects/:projectId/seo" element={<SeoRedirect />} />
          <Route path="projects/:projectId/chatbot" element={<ChatbotPage />} />
          <Route path="projects/:projectId/webhooks" element={<WebhooksPage />} />
          <Route path="projects/:projectId/audit" element={<AuditLogPage />} />
          <Route path="projects/:projectId/requests" element={<RequestDashboardPage />} />
          <Route path="ai-training" element={<AITrainingPage />} />
          <Route
            path="users"
            element={
              <RequireSuperAdmin>
                <UsersPage />
              </RequireSuperAdmin>
            }
          />
          <Route path="plugins" element={<PluginsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="settings/team" element={<TeamSettingsPage />} />
          <Route path="settings/billing" element={<BillingSettingsPage />} />
          <Route path="settings/billing/invoices" element={<BillingInvoicesPage />} />
          <Route
            path="admin/*"
            element={
              <RequireSuperAdmin>
                <Routes>
                  <Route path="sre" element={<SREDashboardPage />} />
                  <Route path="billing" element={<AdminBillingPage />} />
                  <Route path="payment-links" element={<PaymentLinksPage />} />
                  <Route path="costs" element={<CostMonitorPage />} />
                  <Route path="insights" element={<PlatformInsightsPage />} />
                  <Route path="integrations" element={<AdminIntegrationsPage />} />
                  <Route path="subscriber-register" element={<SubscriberRegisterPage />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </RequireSuperAdmin>
            }
          />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </ConfirmProvider>
  );
}
